/**
 * 网络配置
 * 集中管理所有网络请求相关的配置、拦截器和工具
 */
import axios, { AxiosInstance, AxiosError } from 'axios';
import { recordApiErrorFromAxios } from '../utils/apiLastError';

// 确定当前环境
const env = process.env.NODE_ENV || 'development';

// 基础配置
const baseConfig = {
  BASE_URL: '',
  API_PREFIX: '/api',
  TIMEOUT: 15000,
  RETRY_COUNT: 3,
  RETRY_DELAY: 2000,
};

// 开发环境配置：使用空字符串走相对路径，由 setupProxy 转发到后端 50045，避免直连后端导致 ERR_CONNECTION_REFUSED
const developmentConfig = {
  BASE_URL: process.env.REACT_APP_API_BASE_URL ?? '',
  DEBUG: false,
};

// 生产环境配置
const productionConfig = {
  BASE_URL: (() => {
    // 如果环境变量未设置，使用空字符串（相对路径），通过 nginx 的 /api 代理到后端
    const url = process.env.REACT_APP_API_URL || process.env.REACT_APP_API_BASE_URL || '';
    if (process.env.NODE_ENV === 'production' && url && url.includes('localhost')) {
      throw new Error('🚨 生产环境不能使用 localhost');
    }
    return url;
  })(),
  DEBUG: false,
};

// 创建最终配置
const createConfig = () => {
  let config = { ...baseConfig };
  if (env === 'development') {
    config = { ...config, ...developmentConfig };
  } else if (env === 'production') {
    config = { ...config, ...productionConfig };
  }
  return config;
};

const finalConfig = createConfig();

export const API_CONFIG = {
  BASE_URL: finalConfig.BASE_URL,
  API_PREFIX: finalConfig.API_PREFIX,
  TIMEOUT: finalConfig.TIMEOUT,
  RETRY_COUNT: finalConfig.RETRY_COUNT,
  RETRY_DELAY: finalConfig.RETRY_DELAY
};

// 创建axios实例
export const createApiClient = (): AxiosInstance => {
  const baseURL = `${API_CONFIG.BASE_URL}${API_CONFIG.API_PREFIX}`;

  const instance = axios.create({
    baseURL,
    timeout: API_CONFIG.TIMEOUT,
    headers: {
      'Content-Type': 'application/json'
    }
  });

  // 门店端：企微 msOrg JWT 存于 `token`；勿用 pickTokenFromStorage，避免误带 mslogin_token
  instance.interceptors.request.use(
    (config) => {
      const headers = (config.headers ?? {}) as Record<string, string>;
      try {
        const t =
          (typeof localStorage !== 'undefined' && (localStorage.getItem('token') || sessionStorage.getItem('token'))) ||
          null;
        if (t) {
          headers.Authorization = `Bearer ${t}`;
        }
      } catch {
        /* ignore */
      }
      headers['X-Central-Client'] = 'portal';
      config.headers = headers as typeof config.headers;
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  // 响应拦截器
  instance.interceptors.response.use(
    (response) => {
      return response;
    },
    async (error: AxiosError) => {
      recordApiErrorFromAxios(error);
      // 401 仅透传，不再跳转登录页（鉴权与 token 由后端维护）
      return Promise.reject(error);
    }
  );

  return instance;
};

// 创建默认API实例
export const apiClient = createApiClient();

export default {
  apiClient: apiClient,
  apiConfig: API_CONFIG,
  createApiClient,
} as const;
